import { ReportExportError, downloadReportBlob } from "./reportExportApi";

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
}

function apiError(message, status, payload) {
  const error = new Error(message || `Request failed (${status})`);
  error.status = status;
  error.payload = payload || null;
  error.code = payload && typeof payload.code === "string" ? payload.code : "";
  return error;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    ...options,
  });
  const payload = await parseJsonSafe(response);
  if (!response.ok) {
    throw apiError(payload?.error, response.status, payload);
  }
  return payload;
}

function areaQuery(areaId) {
  return `area_id=${encodeURIComponent(areaId)}`;
}

export async function listReportWorkspaces(areaId) {
  const payload = await requestJson(`/api/reports/workspaces?${areaQuery(areaId)}`);
  return Array.isArray(payload?.workspaces) ? payload.workspaces : [];
}

export async function createReportWorkspace({ areaId, title, note = "" }) {
  const payload = await requestJson("/api/reports/workspaces", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ area_id: areaId, title, note }),
  });
  return payload?.workspace || null;
}

export async function getReportWorkspace(areaId, workspaceId) {
  const payload = await requestJson(`/api/reports/workspaces/${workspaceId}?${areaQuery(areaId)}`);
  return payload?.workspace || null;
}

export async function updateReportWorkspace(areaId, workspaceId, patch) {
  const payload = await requestJson(`/api/reports/workspaces/${workspaceId}?${areaQuery(areaId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ area_id: areaId, ...patch }),
  });
  return payload?.workspace || null;
}

export async function archiveReportWorkspace(areaId, workspaceId) {
  await fetch(`/api/reports/workspaces/${workspaceId}?${areaQuery(areaId)}`, {
    method: "DELETE",
    credentials: "include",
  }).then(async (response) => {
    if (!response.ok) {
      throw apiError((await parseJsonSafe(response))?.error, response.status, null);
    }
  });
}

export async function addReportWorkspaceItem(areaId, workspaceId, item) {
  const payload = await requestJson(`/api/reports/workspaces/${workspaceId}/items?${areaQuery(areaId)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ area_id: areaId, ...item }),
  });
  return payload?.item || null;
}

export async function updateReportWorkspaceItem(areaId, workspaceId, itemId, patch) {
  const {
    title,
    note,
    includeInExport,
    orientation,
    preview,
  } = patch || {};
  const payload = await requestJson(`/api/reports/workspaces/${workspaceId}/items/${itemId}?${areaQuery(areaId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ area_id: areaId, title, note, includeInExport, orientation, preview }),
  });
  return payload?.item || null;
}

export async function deleteReportWorkspaceItem(areaId, workspaceId, itemId) {
  await fetch(`/api/reports/workspaces/${workspaceId}/items/${itemId}?${areaQuery(areaId)}`, {
    method: "DELETE",
    credentials: "include",
  }).then(async (response) => {
    if (!response.ok) {
      throw apiError((await parseJsonSafe(response))?.error, response.status, null);
    }
  });
}

export async function reorderReportWorkspaceItems(areaId, workspaceId, itemIds) {
  const payload = await requestJson(`/api/reports/workspaces/${workspaceId}/items/reorder?${areaQuery(areaId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ area_id: areaId, itemIds }),
  });
  return Array.isArray(payload?.items) ? payload.items : [];
}

function getFilenameFromDisposition(disposition) {
  const value = String(disposition || "");
  const quoted = value.match(/filename="([^"]+)"/i);
  if (quoted) return quoted[1];
  const plain = value.match(/filename=([^;]+)/i);
  return plain ? plain[1].trim() : "electionmap-report.pdf";
}

async function parseExportError(response) {
  const payload = await parseJsonSafe(response);
  return {
    message: payload && typeof payload.error === "string" ? payload.error : `Export failed (${response.status})`,
    code: payload && typeof payload.code === "string" ? payload.code : "",
  };
}

function classifyExportError(status, code) {
  if (status === 401 || status === 403 || status === 400 || code === "validation_error" || code === "authorization_error") {
    return "validation";
  }
  return "runtime";
}

export async function exportReportWorkspacePdf(areaId, workspaceId) {
  const response = await fetch(`/api/reports/workspaces/${workspaceId}/export-pdf?${areaQuery(areaId)}`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/pdf",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ area_id: areaId }),
  });

  if (!response.ok) {
    const errorPayload = await parseExportError(response);
    throw new ReportExportError(errorPayload.message, {
      status: response.status,
      code: errorPayload.code || (response.status >= 500 ? "exporter_failure" : "validation_error"),
      kind: classifyExportError(response.status, errorPayload.code),
    });
  }

  return {
    blob: await response.blob(),
    filename: getFilenameFromDisposition(response.headers.get("Content-Disposition")),
  };
}

export { downloadReportBlob };
